import { combine, createApi, createDomain, sample } from 'effector';
import { createGate } from 'effector-react';
import memoizeOne from 'memoize-one';
import { toPercent } from 'pano-utils';
import type { KrpCompareData, KrpPlayer, SceneCompareItem } from '@types';
import getPanoUrl from '@utils/getPanoUrl';
import { $loadedScene } from '@stores/loadedStore';
import { objectKeys } from '@utils/objectKeys';
import { parseMultires } from '@utils/parseMultires';
import { COMPARE_ORIG, COMPARE_TARGET } from './constants';
import type { CompareItemData } from './utils';
import type { CompareMode } from './types';

const store: {
  player: KrpPlayer | null;
  origData: KrpCompareData | null;
  targetData: KrpCompareData | null;
} = {
  player: null,
  origData: null,
  targetData: null,
};

const domain = createDomain('compare');

export const compareReset = domain.event();
compareReset.watch(() => {
  if (!ComparePlayer.isRegistered()) return;
  ComparePlayer.remove();
});

export const $compareSelected = domain
  .store<SceneCompareItem['id'] | null>(null)
  .reset(compareReset);
export const $compareSelectedApi = createApi($compareSelected, {
  set: (_, payload: SceneCompareItem['id'] | null) => payload,
});

export const $compareLocked = domain.store<boolean>(true).reset(compareReset);
export const $compareLockedApi = createApi($compareLocked, {
  set: (_, payload: boolean) => payload,
});
$compareLockedApi.set.watch((locked) => {
  if (!ComparePlayer.isRegistered()) return;
  if (locked) {
    ComparePlayer.lockView();
  } else {
    ComparePlayer.unlockView();
  }
});

export const updateCompareOffset =
  domain.event<SceneCompareItem['viewOffset']>();

export const $GateCompareEditingData = createGate<
  | (CompareItemData & {
      viewOffset?: SceneCompareItem['viewOffset'];
    })
  | null
>({
  domain,
  defaultState: null,
});
export const $GateCompareItemData = createGate<
  | (CompareItemData & {
      viewOffset?: SceneCompareItem['viewOffset'];
    })
  | null
>({
  domain,
  defaultState: null,
});

const $comparePlayerReady = domain.store(false).reset(compareReset);
const $comparePlayerReadyApi = createApi($comparePlayerReady, {
  set: (_, payload: boolean) => payload,
});

const $comparingData = combine(
  $GateCompareEditingData.state,
  $GateCompareItemData.state,
  (editing, lastItem) => {
    if (editing) return editing;
    if (lastItem) return lastItem;
    return null;
  }
);

export const $showCompareWidget = combine(
  $comparingData,
  $comparePlayerReady,
  (data, ready) => {
    return !!data?.data && ready;
  }
);

export const $showCompareLockButton = combine(
  $GateCompareEditingData.state,
  $comparePlayerReady,
  (editing, ready) => {
    return !!editing?.data && ready;
  }
);

export const $showEmptyTip = combine(
  $GateCompareEditingData.state,
  $comparePlayerReady,
  (editing, ready) => {
    return editing?.data === null && ready;
  }
);

export const $compareState = domain
  .store<{
    mode: CompareMode;
    left: number;
    top: number;
    alpha: number;
  }>({
    mode: 'horizontal',
    left: 0.5,
    top: 0.5,
    alpha: 0.5,
  })
  .reset(compareReset);
export const $compareStateApi = createApi($compareState, {
  setMode: (state, payload: CompareMode) => ({
    ...$compareState.defaultState,
    mode: payload,
  }),
  setLeft: (state, payload: number) => ({ ...state, left: payload }),
  setTop: (state, payload: number) => ({ ...state, top: payload }),
  setAlpha: (state, payload: number) => ({ ...state, alpha: payload }),
  reset: () => $compareState.defaultState,
});

export const ComparePlayer = {
  register: (player: KrpPlayer) => {
    if (store.player) {
      ComparePlayer.remove();
    }
    store.player = player;
  },
  isRegistered: () => {
    return !!store.player;
  },
  get: () => {
    if (!store.player) throw new Error('ComparePlayer is not registered');
    return store.player;
  },
  remove: () => {
    store.player?.removePlayer();
    store.player = null;
    store.origData = null;
    store.targetData = null;
    $comparePlayerReadyApi.set(false);
  },
  init: () => {
    const loadedScene = $loadedScene.getState();
    if (!loadedScene) throw new Error('未加载场景');

    const origData: KrpCompareData = {
      id: COMPARE_ORIG,
      width: '100%',
      height: '100%',
      x: '0%',
      y: '0%',
      type: 0,
      alpha: 1,
      url: getPanoUrl({
        cdnDomain: loadedScene.cdnDomain,
        path: loadedScene.path,
      }),
      multires: loadedScene.multires,
      view: loadedScene.view,
    };
    const targetData: KrpCompareData = {
      id: COMPARE_TARGET,
      width: '0%',
      height: '100%',
      x: '0%',
      y: '0%',
      type: 0,
      alpha: 1,
      url: '',
      multires: {},
      view: loadedScene.view,
    };
    store.origData = origData;
    store.targetData = targetData;
    ComparePlayer.get().loadComparePanos([
      {
        ...origData,
        onLoadComplete: () => {
          $comparePlayerReadyApi.set(true);
        },
      },
      targetData,
    ]);
  },
  updateOrig: (data: KrpCompareData) => {
    if (!store.origData) return;
    const updateValues = getUpdatedValues(store.origData, data);
    const newData = { id: COMPARE_ORIG, ...updateValues };
    ComparePlayer.get().updateComparePano(newData);
    store.origData = { ...store.origData, ...updateValues };
  },
  updateTarget: (data: KrpCompareData) => {
    if (!store.targetData) return;
    const updateValues = getUpdatedValues(store.targetData, data);
    const newData = { id: COMPARE_TARGET, ...updateValues };
    ComparePlayer.get().updateComparePano(newData);
    store.targetData = { ...store.targetData, ...updateValues };
  },
  getViewOffset: () => {
    const origView = ComparePlayer.get().getView({
      comparePanoId: COMPARE_ORIG,
    });
    const targetView = ComparePlayer.get().getView({
      comparePanoId: COMPARE_TARGET,
    });
    return {
      hLookAt: targetView.hlookat - origView.hlookat,
      vLookAt: targetView.vlookat - origView.vlookat,
      fov: targetView.fov - origView.fov,
    };
  },
  lockView: () => {
    const viewOffset = ComparePlayer.getViewOffset();
    updateCompareOffset(viewOffset);
    ComparePlayer.get().updateComparePano({
      id: COMPARE_TARGET,
      viewOffset,
    });
    ComparePlayer.get().lockComparePanosView();
  },
  unlockView: () => {
    ComparePlayer.get().unlockComparePanosView({ lockFov: true });
  },
  resetOffset: (viewOffset: SceneCompareItem['viewOffset']) => {
    ComparePlayer.get().updateComparePano({
      id: COMPARE_TARGET,
      viewOffset,
    });
  },
};

function getUpdatedValues(
  orig: KrpCompareData,
  data: KrpCompareData
): KrpCompareData {
  const updatedValues: any = {};

  objectKeys(data).forEach((key) => {
    if (data[key] !== orig[key]) {
      updatedValues[key] = data[key];
    }
  });

  return updatedValues as KrpCompareData;
}

const getMultires = memoizeOne((str: string) => {
  return parseMultires(str);
});

sample({
  source: {
    loadedScene: $loadedScene,
    comparingData: $comparingData,
    comparePlayerReady: $comparePlayerReady,
    compareState: $compareState,
  },
  clock: [$comparePlayerReady, $comparingData, $compareState],
}).watch(({ loadedScene, comparingData, comparePlayerReady, compareState }) => {
  if (!comparePlayerReady || !loadedScene) return;
  if (!comparingData) {
    ComparePlayer.updateOrig({
      width: '100%',
      height: '100%',
      x: '0%',
      y: '0%',
      type: 0,
      alpha: 1,
      view: loadedScene.view,
    });
    ComparePlayer.updateTarget({
      width: '0%',
      height: '100%',
      x: '100%',
      y: '0%',
      type: 0,
      alpha: 1,
      url: '',
      multires: {},
      view: loadedScene.view,
      viewOffset: {
        hLookAt: 0,
        vLookAt: 0,
        fov: 0,
      },
    });
  } else {
    const { mode, left, top, alpha } = compareState;

    // 如果当前没有对比场景, 初始化各种尺寸

    const origData: KrpCompareData = {
      width: '100%',
      height: '100%',
      x: '0%',
      y: '0%',
      alpha: 1,
      type: 0,
      view: loadedScene.view,
    };
    const targetData: KrpCompareData = {
      width: '100%',
      height: '100%',
      x: '0%',
      y: '0%',
      alpha: 1,
      type: 0,
      url: comparingData.data
        ? getPanoUrl({
            path: comparingData.data.path,
            cdnDomain: comparingData.data.cdnDomain,
          })
        : '',
      multires: comparingData.data
        ? getMultires(comparingData.data.multires)
        : {},
      view: loadedScene.view,
      viewOffset: comparingData.viewOffset,
    };

    if (mode === 'horizontal') {
      origData.width = toPercent(left);
      targetData.width = toPercent(1 - left);
      targetData.x = toPercent(left);
    } else if (mode === 'vertical') {
      origData.height = toPercent(top);
      targetData.height = toPercent(1 - top);
      targetData.y = toPercent(top);
    } else if (mode === 'split') {
      origData.width = toPercent(left);
      origData.type = 1;
      targetData.width = toPercent(1 - left);
      targetData.x = toPercent(left);
      targetData.type = 1;
    } else if (mode === 'overlap') {
      origData.alpha = 1 - alpha;
      targetData.alpha = alpha;
    }

    ComparePlayer.updateOrig(origData);
    ComparePlayer.updateTarget(targetData);
  }
});
